import React, { useState, useEffect } from "react"

//controls
import { Button, Col, Row, Label } from "reactstrap"

//react form
import { FormProvider, useForm, Controller } from "react-hook-form"

//navigation
import { Link, useNavigate, useParams } from "react-router-dom"

//validation
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"

//redux
import { useSelector, useDispatch } from "react-redux"

//custom hooks
import { useDeepCompareEffect, useUserDetail } from "hooks"

import Select from "react-select"

//Date filter
import Moment from "moment"

import {
  addHousehold as onAddHousehold,
  updateHousehold as onUpdateHousehold,
} from "store/actions"
import Spinners from "components/Common/Spinner"
import { isEmpty } from "lodash"

import {
  TextInput,
  DropdownInput,
  DateInput,
  NumberInput,
  ToggleInput,
} from "components/Common/Controls"
import { EnumRoleTypes } from "helpers/enum_helper"

function BasicInfoTab({ household, loading }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(loading)
  const [state, setState] = useState({
    selectedSubjects: [],
  })

  const updateState = data => setState(previous => ({ ...previous, ...data }))

  //////////////////HOUSEHOLD STATE/////////////////////
  const { trainingsubjects, trainingtypes, trainingmonths } = useSelector(
    state => state.household
  )
  ///////////////////////////////////////

  /**
   * Form Validation Schema
   */
  const schema = yup.object().shape({
    TrainingMonths: yup.object().required("Required"),
    TrainingTypes: yup.object().required("Required"),
    TrainingSubjects: yup.array(),
    TrainingDate: yup.string().required("Required"),
    Postcode: yup.string().required("Required"),
    ChildrenCount: yup.number().nullable(),
    PensionerCount: yup.number().nullable(),
    DisabledCount: yup.number().nullable(),
    OtherCount: yup.number().nullable(),
    Footfall: yup.number().nullable(),
    Venue: yup.string().required("Required"),
    Delivery: yup.string().nullable(),
    IsActive: yup.bool(),
  })

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      TrainingSubjects: [],
      TrainingDate: "",
      Postcode: "",
      ChildrenCount: 0,
      PensionerCount: 0,
      DisabledCount: 0,
      OtherCount: 0,
      FootfallCount: 0,
      Venue: "",
      Delivery: "",
      IsActive: true,
    },
    resolver: yupResolver(schema),
  })
  const { reset, control, formState, setValue, getValues, trigger, watch } =
    methods
  const { errors, isValid } = formState

  useDeepCompareEffect(() => {
    function updateProfileState() {
      if (!isEmpty(household)) {
        if (trainingsubjects.length > 0) {
          var selectedIds = household.TrainingSubjectIDs
            ? household.TrainingSubjectIDs.split(",").map(id =>
                parseInt(id, 10)
              )
            : []

          const filtered = trainingsubjects.filter(s =>
            selectedIds.includes(s.TrainingSubjectID)
          )
          updateState({ selectedSubjects: filtered })
        } else {
          updateState({ selectedSubjects: trainingsubjects })
        }
      }

      //reset form with server data
      reset(household)
    }

    updateProfileState()
  }, [dispatch, trainingsubjects])

  // const onSubjectSelect = e => {
  //   const { value, checked } = e.target

  //   const existing = [...state.selectedSubjects]
  //   if (checked) {
  //     existing.push(value)
  //     updateState({ selectedSubjects: existing })
  //   } else {
  //     const updatedValue = existing.filter(x => {
  //       return x !== value
  //     })
  //     updateState({ selectedSubjects: updatedValue })
  //   }
  // }

  useEffect(() => {
    setValue("TrainingSubjects", state.selectedSubjects)
  }, [state])

  const onCancelClick = () => {
    navigate("/households")
  }

  const handleSave = () => {
    trigger() //for form validation
    if (isValid) {
      if (isEmpty(household)) {
        dispatch(onAddHousehold(getValues()))
      } else {
        dispatch(onUpdateHousehold(getValues()))
      }
      if (!isEmpty(id)) {
        navigate("/households")
      }
    }
  }

  if (isLoading) {
    return <Spinners setLoading={setLoading} />
  }

  return (
    <FormProvider {...methods}>
      <Row>
        <Col sm="4">
          <div className="mb-3">
            <DropdownInput
              label="Month"
              id="TrainingMonths"
              required={true}
              items={trainingmonths}
              itemlabel="TrainingMonth"
              itemvalue="TrainingMonthID"
            />
          </div>
          <div className="mb-3">
            <NumberInput label="Households with Children" id="ChildrenCount" />
          </div>
          <div className="mb-3">
            <NumberInput
              label="Households with Disabled Person"
              id="DisabledCount"
            />
          </div>
          <div className="mb-3">
            <DateInput
              label="Training Date"
              id="TrainingDate"
              required={true}
            />
          </div>
          <div className="mb-3">
            <DropdownInput
              label="Training Type"
              id="TrainingTypes"
              required={true}
              items={trainingtypes}
              itemlabel="TrainingType"
              itemvalue="TrainingTypeID"
            />
          </div>
        </Col>
        <Col sm="4">
          <div className="mb-3">
            <TextInput
              label="Postcode Area"
              id="Postcode"
              placeholder="e.g. M1, B1"
              required={true}
            />
          </div>

          <div className="mb-3">
            <NumberInput
              label="Households with Pensioners"
              id="PensionerCount"
            />
          </div>
          <div className="mb-3">
            <NumberInput label="Other Households" id="OtherCount" />
          </div>
          <div className="mb-3">
            <NumberInput label="Footfall" id="FootfallCount" />
          </div>
          <div className="mb-3">
            <TextInput
              label="Venue Name"
              id="Venue"
              placeholder="Training Venue"
              required={true}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <div className="mb-3">
          <ToggleInput label="Active" id="IsActive" />
        </div>
      </Row>
      <Row>
        <Col sm="8" className="mb-3">
          <Label>Training Subjects</Label>
          <Controller
            name="TrainingSubjects"
            control={control}
            render={({ field }) => (
              <>
                <Select
                  {...field}
                  id="TrainingSubjects"
                  options={trainingsubjects}
                  getOptionLabel={option => option.TrainingSubject}
                  getOptionValue={option => option.TrainingSubjectID}
                  classNamePrefix="select2-selection"
                  isMulti
                  onChange={(e, { value }) => {
                    const values = e.map(v =>
                      trainingsubjects.find(
                        item => item.TrainingSubjectID === v.TrainingSubjectID
                      )
                    )
                    setValue("TrainingSubjects", values)
                  }}
                />
              </>
            )}
          />
          <div className="mb-3">
            <TextInput
              label="Delivery Postcode"
              id="Delivery"
              placeholder="e.g. B23 5JD"
              required={true}
            />
          </div>
          {/* {trainingsubjects && trainingsubjects.length > 0 && (
            <div className="mb-3">
              <Label>Training Subjects</Label>
              {trainingsubjects.map((type, i) => (
                <div
                  className="form-check form-check-primary d-flex align-items-center mb-3"
                  key={`subject-${type.TrainingSubjectID}-${i}`}
                >
                  <input
                    type="checkbox"
                    className="form-check-input checkbox-medium me-2"
                    value={type.TrainingSubjectID}
                    id={`dvSubject${i}`}
                    onChange={e => onSubjectSelect(e)}
                    autoComplete="off"
                    checked={state.selectedSubjects.includes(
                      type.TrainingSubjectID
                    )}
                  />
                  <label className="form-check-label" htmlFor={`dvSubject${i}`}>
                    {`${type.TrainingSubject}`}
                  </label>
                </div>
              ))}
            </div>
          )} */}
        </Col>
      </Row>
      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" className="btn" onClick={handleSave}>
          {isEmpty(household) ? "Save" : "Update"}
        </Button>
        <Button
          type="submit"
          color="secondary"
          onClick={onCancelClick}
          className=""
        >
          Cancel
        </Button>
      </div>
    </FormProvider>
  )
}
export default BasicInfoTab
